
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useGetCategoriesWithDetails } from '@/hooks/use-categories';
import { useCreateCategory, useDeleteCategory } from '@/hooks/use-category-management';
import { toast } from '@/hooks/use-toast';
import { FileSpreadsheet, Plus, Upload, Trash2, Edit } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CategoryUpload from './CategoryUpload';
import { EditCategoryDialog } from './category/EditCategoryDialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CATEGORY_TYPES, CATEGORY_TYPE_COLORS, CategoryType } from '@/constants/categoryTypes';

const CategoryManagement = () => {
  const { data: categories, isLoading, refetch } = useGetCategoriesWithDetails();
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryType, setNewCategoryType] = useState<CategoryType>('Other');
  const createCategory = useCreateCategory();
  const deleteCategory = useDeleteCategory();
  
  const [editingCategory, setEditingCategory] = useState<{ name: string; category_type: CategoryType } | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    
    try {
      await createCategory.mutateAsync({ name: newCategory, categoryType: newCategoryType });
      toast({
        title: 'Category Added',
        description: `${newCategory} has been added successfully.`,
      });
      setNewCategory('');
      setNewCategoryType('Other');
      refetch();
    } catch (error) {
      console.error('Failed to add category:', error);
      toast({
        title: 'Error',
        description: 'Failed to add category. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    try {
      await deleteCategory.mutateAsync(categoryName);
      toast({
        title: 'Category Removed',
        description: `${categoryName} has been removed successfully.`,
      });
      refetch();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove category. Please try again.',
        variant: 'destructive',
      });
    }
  };
  
  const handleEditCategory = (category: { name: string; category_type: CategoryType }) => {
    setEditingCategory(category);
    setEditDialogOpen(true);
  };

  const handleUploadComplete = () => {
    toast({
      title: 'Categories Updated',
      description: 'Your category list has been refreshed with the uploaded categories.',
    });
    refetch();
  };

  const getCategoryTypeBadgeClass = (type: CategoryType) => {
    return CATEGORY_TYPE_COLORS[type] || CATEGORY_TYPE_COLORS.Other;
  };

  return (
    <div>
      <Tabs defaultValue="list">
        <TabsList className="mb-4">
          <TabsTrigger value="list">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Category List
          </TabsTrigger>
          <TabsTrigger value="upload">
            <Upload className="h-4 w-4 mr-2" />
            Upload Categories
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="list">
          <div className="space-y-6">
            <form onSubmit={handleAddCategory} className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter category name"
                  className="w-full"
                />
              </div>
              <Select value={newCategoryType} onValueChange={(value) => setNewCategoryType(value as CategoryType)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" disabled={createCategory.isPending}>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </form>
            
            {isLoading ? (
              <div className="py-8 text-center text-muted-foreground">Loading categories...</div>
            ) : categories && categories.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getCategoryTypeBadgeClass(category.category_type)}>
                          {category.category_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={category.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'}>
                          {category.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditCategory({ name: category.name, category_type: category.category_type })}
                          >
                            <Edit className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteCategory(category.name)}
                            disabled={deleteCategory.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Card className="p-8 text-center text-muted-foreground">
                No categories added yet. Add your first category above.
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="upload">
          <CategoryUpload onUploadComplete={handleUploadComplete} />
        </TabsContent>
      </Tabs>
      
      <EditCategoryDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        category={editingCategory}
        onCategoryUpdated={refetch}
      />
    </div>
  );
}

export default CategoryManagement;
